import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { CalendarDays, Users, BookOpen, UserCircle2, Save } from "lucide-react";
import { toast } from "sonner";

export default function MemberDashboard() {
  const { user, updateProfile } = useAuth();
  const [upcoming, setUpcoming] = useState([]);
  const [members, setMembers] = useState([]);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get("/events", { params: { scope: "upcoming" } }).then((r) => setUpcoming(r.data.slice(0, 4))).catch(() => {});
    api.get("/members").then((r) => setMembers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) setProfile({
      name: user.name || "",
      credentials: user.credentials || "",
      specialty: user.specialty || "",
      city: user.city || "",
      bio: user.bio || "",
    });
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    const res = await updateProfile(profile);
    if (res.ok) toast.success("Profile updated."); else toast.error(res.error);
  };

  if (!user || !profile) return null;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="overline">Member portal</div>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl">Hello, {user.name.split(",")[0]}.</h1>
          <p className="mt-3 text-[color:var(--azsam-ink-soft)] max-w-2xl">
            Welcome back. Your portal gives you access to CME registration, the statewide member
            directory, and clinical resources.
          </p>
        </div>
        <div className="text-xs uppercase tracking-widest px-4 py-2 rounded-full bg-[color:var(--azsam-sage)]/15 text-[color:var(--azsam-sage-hover)] font-semibold" data-testid="dashboard-role">
          {user.role === "admin" ? "Administrator" : "Member in good standing"}
        </div>
      </div>

      <div className="mt-12 grid lg:grid-cols-3 gap-8">
        {/* Upcoming events */}
        <div className="lg:col-span-2 azsam-card p-8">
          <div className="flex items-center gap-2 overline"><CalendarDays size={14} /> Upcoming events</div>
          <div className="mt-6 space-y-4">
            {upcoming.map((e) => (
              <Link to={`/events/${e.id}`} key={e.id} className="flex gap-4 p-4 rounded-lg hover:bg-[color:var(--azsam-cream)] transition" data-testid={`dashboard-event-${e.id}`}>
                <div className="w-14 h-14 rounded-lg bg-[color:var(--azsam-clay)]/10 text-[color:var(--azsam-clay)] flex flex-col items-center justify-center font-heading">
                  <div className="text-xs uppercase tracking-widest">{new Date(e.date + "T00:00:00").toLocaleString("en-US", { month: "short" })}</div>
                  <div className="text-lg leading-none">{new Date(e.date + "T00:00:00").getDate()}</div>
                </div>
                <div className="flex-1">
                  <div className="font-heading text-lg">{e.title}</div>
                  <div className="text-xs text-[color:var(--azsam-ink-soft)] mt-1">{e.location} · {e.format}{e.cme_credits > 0 && ` · ${e.cme_credits} CME`}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Quick links */}
        <div className="azsam-card p-8">
          <div className="flex items-center gap-2 overline"><BookOpen size={14} /> Shortcuts</div>
          <div className="mt-6 space-y-3">
            <Link to="/directory" className="block p-4 rounded-lg border border-[color:var(--azsam-stone)] hover:border-[color:var(--azsam-clay)]" data-testid="dashboard-link-directory">
              <div className="flex items-center gap-2 font-heading text-lg"><Users size={16} /> Member directory</div>
              <div className="text-xs text-[color:var(--azsam-ink-soft)] mt-1">{members.length} clinicians statewide</div>
            </Link>
            <Link to="/resources" className="block p-4 rounded-lg border border-[color:var(--azsam-stone)] hover:border-[color:var(--azsam-clay)]" data-testid="dashboard-link-resources">
              <div className="flex items-center gap-2 font-heading text-lg"><BookOpen size={16} /> Clinical resources</div>
              <div className="text-xs text-[color:var(--azsam-ink-soft)] mt-1">Toolkits, coding guides, evidence reviews</div>
            </Link>
            <Link to="/events" className="block p-4 rounded-lg border border-[color:var(--azsam-stone)] hover:border-[color:var(--azsam-clay)]" data-testid="dashboard-link-events">
              <div className="flex items-center gap-2 font-heading text-lg"><CalendarDays size={16} /> All events &amp; CME</div>
            </Link>
            {user.role === "admin" && (
              <Link to="/admin" className="block p-4 rounded-lg border-2 border-[color:var(--azsam-clay)] bg-[color:var(--azsam-clay)]/5 hover:bg-[color:var(--azsam-clay)]/10" data-testid="dashboard-link-admin">
                <div className="flex items-center gap-2 font-heading text-lg text-[color:var(--azsam-clay)]"><Save size={16} /> Admin console</div>
                <div className="text-xs text-[color:var(--azsam-ink-soft)] mt-1">Manage events, resources, news, committees, sponsors</div>
              </Link>
            )}
          </div>
        </div>

        {/* Profile */}
        <form onSubmit={saveProfile} className="azsam-card p-8 lg:col-span-3" data-testid="dashboard-profile-form">
          <div className="flex items-center gap-2 overline"><UserCircle2 size={14} /> Your directory profile</div>
          <div className="mt-6 grid md:grid-cols-2 gap-5">
            <F label="Full name"><input required value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className={input} data-testid="dashboard-name" /></F>
            <F label="Credentials"><input value={profile.credentials} onChange={(e) => setProfile({ ...profile, credentials: e.target.value })} className={input} data-testid="dashboard-credentials" /></F>
            <F label="Specialty"><input value={profile.specialty} onChange={(e) => setProfile({ ...profile, specialty: e.target.value })} className={input} data-testid="dashboard-specialty" /></F>
            <F label="City"><input value={profile.city} onChange={(e) => setProfile({ ...profile, city: e.target.value })} className={input} data-testid="dashboard-city" /></F>
          </div>
          <F label="Short bio (visible to fellow members)">
            <textarea rows={3} value={profile.bio} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} className={input + " resize-y"} data-testid="dashboard-bio" />
          </F>
          <button type="submit" className="btn-primary mt-6" data-testid="dashboard-save">
            <Save size={16} /> Save profile
          </button>
        </form>
      </div>
    </div>
  );
}

const input = "w-full px-4 py-3 rounded-lg border border-[color:var(--azsam-stone)] focus:outline-none focus:border-[color:var(--azsam-clay)] transition";

function F({ label, children }) {
  return (
    <label className="block mt-2">
      <div className="text-xs uppercase tracking-widest text-[color:var(--azsam-ink-soft)] mb-2 font-semibold">{label}</div>
      {children}
    </label>
  );
}
